import React from 'react';
import InfoTooltip from '../common/InfoTooltip';

interface FormSelectProps {
  name: string;
  label: string;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  required?: boolean;
  description?: string;
  tooltip?: React.ReactNode;
  value?: string | string[];
  onChange?: (value: any) => void;
  onBlur?: () => void;
  error?: string;
  disabled?: boolean;
  className?: string;
  multiple?: boolean;
}

const FormSelect: React.FC<FormSelectProps> = ({
  name,
  label,
  options,
  placeholder,
  required,
  description,
  tooltip,
  value,
  onChange,
  onBlur,
  error,
  disabled,
  className = "",
  multiple = false
}) => {
  // Hooks must be at the top level, before any conditional returns
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    if (!multiple) return; // Only set up listener for multi-select

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [multiple]);

  const handleChange = (val: string) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(val)
        ? currentValues.filter(v => v !== val)
        : [...currentValues, val];
      onChange?.(newValues);
    } else {
      onChange?.(val);
    }
  };

  if (multiple) {
    const currentValues = Array.isArray(value) ? value : [];

    const filteredOptions = options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleRemove = (valueToRemove: string) => {
      const newValues = currentValues.filter(v => v !== valueToRemove);
      onChange?.(newValues);
    };

    return (
      <div className={`flex flex-col ${className}`} ref={dropdownRef}>
        <div className="flex items-center gap-1.5 mb-1.5">
          <label className="text-sm font-black text-gray-900 tracking-tight">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          {tooltip && <InfoTooltip content={tooltip} label="Guidance" />}
        </div>

        {/* Multi-select dropdown container */}
        <div className="relative">
          <div
            className={`min-h-[46px] w-full rounded-xl border px-3 py-2 cursor-pointer transition-all ${isOpen ? 'border-blue-500 ring-4 ring-blue-500/10' : 'border-slate-200'
              } ${error ? 'border-red-500' : ''} ${disabled ? 'bg-gray-50' : 'bg-white'}`}
            onClick={() => !disabled && setIsOpen(!isOpen)}
          >
            {/* Selected items as pills */}
            <div className="flex flex-wrap gap-1.5">
              {currentValues.length === 0 ? (
                <span className="text-sm text-slate-400 font-medium py-0.5">{placeholder || 'Select options...'}</span>
              ) : (
                currentValues.map(val => {
                  const option = options.find(o => o.value === val);
                  return option ? (
                    <span
                      key={val}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!disabled) handleRemove(val);
                      }}
                    >
                      {option.label}
                      <svg className="w-3.5 h-3.5 hover:text-blue-900 cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </span>
                  ) : null;
                })
              )}
            </div>

            {/* Dropdown arrow */}
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg
                className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
              </svg>
            </div>
          </div>

          {/* Dropdown menu */}
          {isOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-hidden">
              {/* Search input */}
              <div className="p-2 border-b border-slate-100">
                <input
                  type="text"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {/* Options list */}
              <div className="max-h-48 overflow-y-auto">
                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-slate-400 text-center">No options found</div>
                ) : (
                  filteredOptions.map(option => {
                    const isSelected = currentValues.includes(option.value);
                    return (
                      <div
                        key={option.value}
                        className={`px-3 py-2 text-sm font-medium cursor-pointer transition-colors flex items-center gap-2 ${isSelected ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-slate-50'
                          }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChange(option.value);
                        }}
                      >
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                          }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        {option.label}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {description && <p className="mt-1.5 text-xs text-gray-400 font-medium leading-relaxed">{description}</p>}
        {error && <p className="mt-1.5 text-xs text-red-500 font-bold">{error}</p>}
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <label className="text-sm font-black text-gray-900 tracking-tight">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        {tooltip && <InfoTooltip content={tooltip} label="Guidance" />}
      </div>
      <div className="relative">
        <select
          className={`w-full appearance-none rounded-xl border border-slate-200 px-4 pr-10 py-3 text-sm font-medium focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all ${error ? 'border-red-500' : 'border-slate-200'
            } ${disabled ? 'bg-gray-50 text-gray-400' : 'bg-white text-gray-800'}`}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
        </svg>
      </div>
      {description && <p className="mt-1.5 text-xs text-gray-400 font-medium leading-relaxed">{description}</p>}
      {error && <p className="mt-1.5 text-xs text-red-500 font-bold">{error}</p>}
    </div>
  );
};

export default FormSelect;
